import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Send, Loader2, RotateCcw, Copy, Check, LogOut, Menu, X,
  Mic, MicOff, Volume2, VolumeX, Phone, PhoneOff, User, MessageSquare,
  Paperclip, FileText, Image, File, XCircle, Home, Sun, Moon,
  PanelLeftClose, PanelLeft, Save
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { chatWithAI } from '../services/api';


// Icono de ondas de audio estilo ChatGPT
const VoiceWaveIcon = ({ isActive, isListening }) => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    {isActive ? (
      <>
        <path d="M12 4v16" className={isListening ? "animate-pulse" : ""} />
        <path d="M8 7v10" className={isListening ? "animate-pulse" : ""} style={{animationDelay: "0.1s"}} />
        <path d="M4 10v4" className={isListening ? "animate-pulse" : ""} style={{animationDelay: "0.2s"}} />
        <path d="M16 7v10" className={isListening ? "animate-pulse" : ""} style={{animationDelay: "0.1s"}} />
        <path d="M20 10v4" className={isListening ? "animate-pulse" : ""} style={{animationDelay: "0.2s"}} />
      </>
    ) : (
      <>
        <path d="M12 8v8" />
        <path d="M8 10v4" />
        <path d="M16 10v4" />
      </>
    )}
  </svg>
);

export default function XIAMainChat() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const userName = user?.full_name?.split(' ')[0] || '';

  // Theme state
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('xia-theme');
    return saved ? saved === 'dark' : true;
  });

  // Sidebar state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('xia-sidebar-width');
    return saved ? parseInt(saved) : 280;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('xia-sidebar-collapsed');
    return saved === 'true';
  });
  const [isResizing, setIsResizing] = useState(false);

  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: userName ? '¡Hola ' + userName + '! Soy XIA, tu asistente de inteligencia artificial. ¿En qué puedo ayudarte hoy?' : '¡Hola! Soy XIA, ¿en qué puedo ayudarte?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [conversationMode, setConversationMode] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('xia-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [input]);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'es-ES';
      recognitionRef.current.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setInput(transcript);
      };
      recognitionRef.current.onend = () => setIsListening(false);
      recognitionRef.current.onerror = () => setIsListening(false);
    }
    return () => recognitionRef.current?.stop();
  }, []);

  // Sidebar resize handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = Math.max(60, Math.min(400, e.clientX));
      setSidebarWidth(newWidth);
      if (newWidth < 100) {
        setSidebarCollapsed(true);
      } else {
        setSidebarCollapsed(false);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const saveConfig = () => {
    localStorage.setItem('xia-sidebar-width', sidebarWidth.toString());
    localStorage.setItem('xia-sidebar-collapsed', sidebarCollapsed.toString());
    localStorage.setItem('xia-theme', darkMode ? 'dark' : 'light');
  };

  const toggleSidebar = () => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      setSidebarWidth(280);
    } else {
      setSidebarCollapsed(true);
      setSidebarWidth(60);
    }
  };

  const toggleListening = async () => {
    if (isListening && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      // Detección de silencio simple con AudioContext
      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      
      let speaking = false;
      let silenceTimer = null;
      
      const detectSound = () => {
        if (recorder.state !== 'recording') return;
        analyser.getByteFrequencyData(data);
        const volume = data.reduce((a,b) => a+b) / data.length;
        
        if (volume > 8) {
          speaking = true;
          if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
        } else if (speaking && !silenceTimer) {
          silenceTimer = setTimeout(() => {
            if (recorder.state === 'recording') recorder.stop();
          }, 1200);
        }
        requestAnimationFrame(detectSound);
      };
      
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        audioCtx.close();
        if (silenceTimer) clearTimeout(silenceTimer);
        
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        audioChunksRef.current = [];
        setIsListening(false);
        
        if (blob.size < 1000) {
          if (conversationMode) setTimeout(toggleListening, 300);
          return;
        }
        
        const formData = new FormData();
        formData.append('audio', blob, 'audio.webm');
        
        try {
          const res = await fetch('/api/voice/transcribe', { method: 'POST', body: formData });
          const result = await res.json();
          if (result.text?.trim()) {
            setInput(result.text);
            setTimeout(() => document.querySelector('form')?.requestSubmit(), 100);
          } else if (conversationMode) {
            setTimeout(toggleListening, 300);
          }
        } catch (e) {
          if (conversationMode) setTimeout(toggleListening, 500);
        }
      };
      
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsListening(true);
      detectSound();
      
    } catch (err) {
      alert('Error micrófono: ' + err.message);
    }
  };

  const toggleConversationMode = () => {
    if (conversationMode) {
      setConversationMode(false);
      setVoiceEnabled(false);
      if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
      window.speechSynthesis?.cancel();
      setIsListening(false);
    } else {
      setConversationMode(true);
      setVoiceEnabled(true);
      toggleListening();
    }
  };

  const allowedTypes = [
    'application/pdf', 'text/plain', 'text/csv', 'application/json',
    'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'text/markdown'
  ];

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return Image;
    if (type.includes('pdf') || type.includes('word') || type.includes('document')) return FileText;
    return File;
  };

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      if (file.type.startsWith('image/')) {
        // Send image as base64 for OCR processing
        reader.onload = () => resolve({
          name: file.name,
          type: file.type,
          content: '[IMAGEN_BASE64:' + reader.result + ']',
          isImage: true,
          dataUrl: reader.result
        });
        reader.onerror = reject;
        reader.readAsDataURL(file);
      } else if (file.type === 'application/pdf') {
        // Send PDF as base64 for text extraction
        reader.onload = () => resolve({
          name: file.name,
          type: file.type,
          content: '[PDF_BASE64:' + reader.result + ']',
          isPdf: true
        });
        reader.onerror = reject;
        reader.readAsDataURL(file);
      } else {
        reader.onload = () => resolve({ name: file.name, type: file.type, content: reader.result });
        reader.onerror = reject;
        reader.readAsText(file);
      }
    });
  };

  const handleFiles = useCallback(async (files) => {
    const validFiles = Array.from(files).filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        alert('El archivo ' + f.name + ' es demasiado grande. Máximo 10MB.');
        return false;
      }
      if (!allowedTypes.some(t => f.type.includes(t.split('/')[1]) || f.type === t)) {
        alert('Tipo de archivo no soportado: ' + f.name);
        return false;
      }
      return true;
    });

    for (const file of validFiles) {
      try {
        const fileData = await readFileContent(file);
        setAttachedFiles(prev => [...prev, fileData]);
      } catch (err) {
        console.error('Error reading file:', err);
      }
    }
  }, []);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!input.trim() && attachedFiles.length === 0) || loading) return;

    let messageContent = input.trim();
    if (attachedFiles.length > 0) {
      const fileContents = attachedFiles.map(f => {
        if (f.isImage) return f.content;
        return '--- Archivo: ' + f.name + ' ---\n' + f.content + '\n--- Fin de ' + f.name + ' ---';
      }).join('\n\n');
      messageContent = messageContent + (messageContent ? '\n\n' : '') + fileContents;
    }

    const displayMessage = {
      role: 'user',
      content: input.trim() || 'Archivos adjuntos',
      files: attachedFiles.map(f => f.name)
    };

    setMessages(prev => [...prev, displayMessage]);
    setInput('');
    setAttachedFiles([]);
    setLoading(true);

    try {
      const response = await chatWithAI(messageContent, messages);
      const aiMessage = response.data.response || response.data.message || 'Sin respuesta';
      setMessages(prev => [...prev, { role: 'assistant', content: aiMessage }]);
      if (voiceEnabled) {
        setIsSpeaking(true);
        // Usar ElevenLabs para voz natural
        try {
          const voiceRes = await fetch('/api/voice/speak', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text: aiMessage })
          });
          const voiceData = await voiceRes.json();
          if (voiceData.url) {
            const audio = new Audio(voiceData.url);
            audio.onended = () => {
              setIsSpeaking(false);
              if (conversationMode) setTimeout(toggleListening, 500);
            };
            audio.onerror = () => setIsSpeaking(false);
            audio.play();
          } else {
            setIsSpeaking(false);
          }
        } catch (e) {
          console.error('ElevenLabs error:', e);
          setIsSpeaking(false);
        }
      }
    } catch (err) {
      console.error('Error en chat:', err);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Lo siento, ha ocurrido un error. Por favor, inténtalo de nuevo.' }]);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const clearChat = () => {
    setMessages([{ role: 'assistant', content: '¡Hola ' + userName + '! Conversación reiniciada. ¿En qué puedo ayudarte?' }]);
    setAttachedFiles([]);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Theme colors
  const theme = {
    bg: darkMode ? 'bg-gray-900' : 'bg-gray-100',
    text: darkMode ? 'text-white' : 'text-gray-900',
    sidebar: darkMode ? 'bg-gray-800' : 'bg-white',
    sidebarBorder: darkMode ? 'border-gray-700' : 'border-gray-200',
    card: darkMode ? 'bg-gray-700' : 'bg-gray-200',
    cardHover: darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-300',
    input: darkMode ? 'bg-gray-700' : 'bg-white border border-gray-300',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    userBubble: darkMode ? 'bg-blue-600' : 'bg-blue-500',
    aiBubble: darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200',
  };

  return (
    <div
      className={'h-screen flex ' + theme.bg + ' ' + theme.text}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drop overlay */}
      {isDragging && (
        <div className="fixed inset-0 bg-blue-500/20 border-4 border-dashed border-blue-500 z-50 flex items-center justify-center">
          <div className={theme.sidebar + ' rounded-2xl p-8 text-center'}>
            <FileText className="w-16 h-16 mx-auto mb-4 text-blue-400" />
            <p className="text-xl font-semibold">Suelta los archivos aquí</p>
            <p className={theme.textMuted + ' mt-2'}>PDF, TXT, DOC, imágenes...</p>
          </div>
        </div>
      )}

      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside
        className={'fixed lg:static inset-y-0 left-0 z-50 flex flex-col border-r transition-all duration-300 ' + theme.sidebar + ' ' + theme.sidebarBorder + ' ' + (sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0') + (sidebarCollapsed ? ' cursor-pointer' : '')}
        style={{ width: sidebarCollapsed ? 60 : sidebarWidth }}
        onClick={sidebarCollapsed ? toggleSidebar : undefined}
      >
        <div className={'p-3 border-b flex items-center ' + theme.sidebarBorder + ' ' + (sidebarCollapsed ? 'justify-center' : 'justify-between')}>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            {!sidebarCollapsed && <span className="font-bold">XIA</span>}
          </div>
          {!sidebarCollapsed && (
            <button onClick={toggleSidebar} className={'p-1.5 rounded-lg ' + theme.cardHover} title="Colapsar sidebar">
              <PanelLeftClose className="w-4 h-4" />
            </button>
          )}
          {sidebarCollapsed && (
            <button onClick={toggleSidebar} className={'p-1.5 rounded-lg ' + theme.cardHover + ' lg:hidden'} title="Expandir sidebar">
              <PanelLeft className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="p-2 space-y-1">
          <button onClick={clearChat} className={'w-full flex items-center gap-2 p-2 rounded-lg ' + theme.card + ' ' + theme.cardHover + ' ' + (sidebarCollapsed ? 'justify-center' : '')} title="Nueva conversación">
            <RotateCcw className="w-4 h-4 flex-shrink-0" />
            {!sidebarCollapsed && <span className="text-sm">Nueva conversación</span>}
          </button>
          <button onClick={() => navigate('/')} className={'w-full flex items-center gap-2 p-2 rounded-lg ' + theme.cardHover + ' ' + (sidebarCollapsed ? 'justify-center' : '')} title="Inicio">
            <Home className="w-4 h-4 flex-shrink-0" />
            {!sidebarCollapsed && <span className="text-sm">Inicio</span>}
          </button>
        </div>

        <div className="flex-1" />

        {/* Bottom buttons */}
        <div className={'p-2 border-t space-y-1 ' + theme.sidebarBorder}>
          <button onClick={() => setDarkMode(!darkMode)} className={'w-full flex items-center gap-2 p-2 rounded-lg ' + theme.cardHover + ' ' + (sidebarCollapsed ? 'justify-center' : '')} title={darkMode ? 'Modo claro' : 'Modo oscuro'}>
            {darkMode ? <Sun className="w-4 h-4 flex-shrink-0" /> : <Moon className="w-4 h-4 flex-shrink-0" />}
            {!sidebarCollapsed && <span className="text-sm">{darkMode ? 'Modo claro' : 'Modo oscuro'}</span>}
          </button>
          <button onClick={saveConfig} className={'w-full flex items-center gap-2 p-2 rounded-lg ' + theme.cardHover + ' ' + (sidebarCollapsed ? 'justify-center' : '')} title="Guardar">
            <Save className="w-4 h-4 flex-shrink-0" />
            {!sidebarCollapsed && <span className="text-sm">Guardar</span>}
          </button>
          <button onClick={handleLogout} className={'w-full flex items-center gap-2 p-2 rounded-lg text-red-400 hover:bg-red-500/20 ' + (sidebarCollapsed ? 'justify-center' : '')} title="Salir">
            <LogOut className="w-4 h-4 flex-shrink-0" />
            {!sidebarCollapsed && <span className="text-sm">Salir</span>}
          </button>
        </div>

        {/* User info */}
        {!sidebarCollapsed && (
          <div className={'p-3 border-t ' + theme.sidebarBorder}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.full_name || 'Usuario'}</p>
                <p className={'text-xs truncate ' + theme.textMuted}>{user?.email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Resize handle */}
        <div
          className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500/50 transition-colors hidden lg:block"
          onMouseDown={handleMouseDown}
        />
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className={'lg:hidden flex items-center justify-between p-3 border-b ' + theme.sidebar + ' ' + theme.sidebarBorder}>
          <button onClick={() => setSidebarOpen(true)} className={'p-2 rounded-lg ' + theme.cardHover}>
            <Menu className="w-5 h-5" />
          </button>
          <span className="font-bold">XIA</span>
          <button onClick={handleLogout} className="p-2 rounded-lg text-red-400 hover:bg-red-500/20">
            <LogOut className="w-5 h-5" />
          </button>
        </header>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={'flex gap-3 ' + (msg.role === 'user' ? 'flex-row-reverse' : '')}>
                <div className={'w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ' + (msg.role === 'user' ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-purple-500 to-purple-600')}>
                  {msg.role === 'user' ? <User className="w-4 h-4 text-white" /> : <MessageSquare className="w-4 h-4 text-white" />}
                </div>
                <div className={'flex-1 px-4 py-3 rounded-2xl max-w-[85%] ' + (msg.role === 'user' ? theme.userBubble + ' text-white ml-auto' : theme.aiBubble)}>
                  <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                  {msg.files && msg.files.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {msg.files.map((f, i) => (
                        <span key={i} className="text-xs bg-black/20 px-2 py-1 rounded">{f}</span>
                      ))}
                    </div>
                  )}
                  {msg.role === 'assistant' && (
                    <button onClick={() => copyToClipboard(msg.content, idx)} className={'mt-2 text-xs flex items-center gap-1 ' + theme.textMuted + ' hover:opacity-80'}>
                      {copiedIndex === idx ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      {copiedIndex === idx ? 'Copiado' : 'Copiar'}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                  <MessageSquare className="w-4 h-4 text-white" />
                </div>
                <div className={theme.aiBubble + ' px-4 py-3 rounded-2xl'}>
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input area */}
        <div className={'border-t p-4 ' + theme.sidebar + ' ' + theme.sidebarBorder}>
          {/* Attached files preview */}
          {attachedFiles.length > 0 && (
            <div className="max-w-3xl mx-auto mb-3 flex flex-wrap gap-2">
              {attachedFiles.map((file, idx) => {
                const FileIcon = getFileIcon(file.type);
                return (
                  <div key={idx} className={'flex items-center gap-2 px-3 py-2 rounded-lg ' + theme.card}>
                    <FileIcon className="w-4 h-4 text-blue-400" />
                    <span className="text-sm truncate max-w-[150px]">{file.name}</span>
                    <button onClick={() => removeFile(idx)} className={theme.textMuted + ' hover:text-red-400'}>
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
            <div className={'flex items-end gap-2 rounded-2xl p-2 ' + theme.input}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleFiles(e.target.files)}
                multiple
                className="hidden"
                accept=".pdf,.txt,.csv,.json,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.md"
              />
              <button type="button" onClick={() => fileInputRef.current?.click()} className={'p-2 rounded-lg transition-colors ' + theme.cardHover + ' ' + theme.textMuted}>
                <Paperclip className="w-5 h-5" />
              </button>
              <button type="button" onClick={toggleConversationMode} className={"p-2 rounded-lg transition-colors flex items-center gap-1 " + (conversationMode ? "bg-green-500 text-white" : theme.cardHover + " " + theme.textMuted)} title={conversationMode ? "Parar conversación" : "Iniciar conversación"}><VoiceWaveIcon isActive={conversationMode} isListening={isListening} /></button>
              <button type="button" onClick={toggleListening} className={'p-2 rounded-lg transition-colors ' + (isListening ? 'bg-red-500 text-white' : theme.cardHover + ' ' + theme.textMuted)}>
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              <button type="button" onClick={() => setVoiceEnabled(!voiceEnabled)} className={'p-2 rounded-lg transition-colors ' + (voiceEnabled ? 'bg-blue-500 text-white' : theme.cardHover + ' ' + theme.textMuted)}>
                {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(e); }}}
                placeholder="Escribe tu mensaje..."
                className="flex-1 bg-transparent resize-none outline-none max-h-32 py-2 px-2 text-sm"
                rows={1}
              />
              <button
                type="submit"
                disabled={(!input.trim() && attachedFiles.length === 0) || loading}
                className={'p-2 rounded-lg transition-colors ' + ((input.trim() || attachedFiles.length > 0) && !loading ? 'bg-blue-500 hover:bg-blue-600 text-white' : theme.card + ' ' + theme.textMuted + ' cursor-not-allowed')}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className={'text-center text-xs mt-2 ' + theme.textMuted}>XIA puede cometer errores. Verifica la información importante.</p>
          </form>
        </div>
      </main>
    </div>
  );
}
